// The page around the scene while a Mac is (or is not) connected: the Live badge, the sheet's Connection part
// (how each Mac's bytes actually travel — the selected ICE pair — and the Relay only (test) switch, which
// reconnects every Mac through a TURN relay), the Connect button and pairing, and installing the app.
//
// `makeLive({ macs, connection, liveStatus, live, versionText, relayAction })` → { setBadge, drawRoutes, drawState,
// pairThisDevice, installControls }; app.js calls `installControls()` once the scene stands.
import { relayOnly, setRelayOnly } from './rtc.js'
import { relayCard } from './relaycard.js'
import { servedByMac } from './transport.js'
import { registerOffline, wireInstall } from './offline.js'
import { routeIcon } from './routeicons.js'
import { phoneBridge, phoneAddMac } from './phonekey.js'

/// `pair`: inside the Even app, Add a Mac with the typed code (even/index.js) — Connect and the cards' Pair go there.
export function makeLive({ macs, connection, liveStatus, live, versionText, relayAction, pair = null }) {
  const badge = document.getElementById('badge')
  let badgeText = ''
  function setBadge(text) {
    badgeText = text
    badge.textContent = text
    badge.classList.add('live'); badge.classList.remove('demo')
    const dot = document.getElementById('dot')
    if (dot) { dot.classList.add('live'); dot.title = text; dot.setAttribute('aria-label', text) }
  }

  /// The sheet's Connection part: how each Mac's bytes actually travel (the selected ICE pair), and the Relay only
  /// (test) switch, which reconnects every Mac through a TURN relay.
  function drawRoutes() {
    const box = document.getElementById('routes')
    if (!box) return
    box.hidden = !connection
    // each Mac: the Relay / Direct chip, the route in words, and under it the candidate types and when it was measured
    const kinds = { host: 'host', srflx: 'through NAT', prflx: 'through NAT', relay: 'relay' }
    const agoText = (t) => { const s = Math.round((Date.now() - t) / 1000); return s < 5 ? 'just now' : s < 90 ? s + ' s ago' : Math.round(s / 60) + ' min ago' }
    const lines = macs.list.map((m) => {
      const el = document.createElement('div'); el.className = 'rl'
      const r = m.link === 'online' && !m.paused ? m.route : null
      if (r) {
        const kind = r.kind === 'relay' ? 'relay' : 'direct'
        const chip = document.createElement('span'); chip.className = 'routechip ' + kind
        chip.innerHTML = routeIcon(kind) + (kind === 'relay' ? 'Relay' : 'Direct')   // the icon's SVG and a fixed word: nothing of the Mac's in it
        el.append(chip)
      }
      const text = document.createElement('span')
      text.textContent = `${m.name}: ${m.paused ? 'paused' : m.link === 'online' ? (m.route?.label || 'finding the route…') : m.relay ? 'needs Cody’s relay' : m.link === 'offline' ? 'offline — ' + (m.why || 'not answering') : 'connecting…'}`
      el.append(text)
      if (r) { const small = document.createElement('small'); small.textContent = `this browser: ${kinds[r.local] || r.local || '?'} · the Mac: ${kinds[r.remote] || r.remote || '?'} · measured ${r.checkedAt ? agoText(r.checkedAt) : '—'}`; el.append(small) }
      return el
    })
    const routeLines = document.getElementById('route-lines')
    if (lines.length) routeLines.replaceChildren(...lines); else routeLines.textContent = 'No Mac yet.'
    const onlyText = document.getElementById('relay-only-text')
    if (onlyText) onlyText.textContent = 'Relay only (test): ' + (relayOnly() ? 'on — every Mac through a relay' : 'off')
    const version = document.getElementById('version-line')
    if (version) version.textContent = 'This app: ' + versionText()
    // Cody's relay refused for a Mac: the same card as on Home, with its button
    const cards = document.getElementById('relay-cards')
    if (cards) {
      cards.replaceChildren(...macs.list.filter((m) => !m.paused && m.link !== 'online' && m.relay).map((m) => {
        const card = relayCard(m.relay)
        const el = document.createElement('div')
        el.className = 'relay-card'
        const title = document.createElement('b'); title.textContent = (macs.several ? m.name + ': ' : '') + card.title
        const detail = document.createElement('p'); detail.textContent = card.detail
        el.append(title, detail)
        if (card.action) {
          const b = document.createElement('button'); b.className = 'link'; b.textContent = card.action.label + ' →'
          b.addEventListener('click', () => relayAction(card.action.act, m.key))
          el.append(b)
        }
        return el
      }))
    }
    const box2 = document.getElementById('relay-only')
    if (box2) box2.checked = relayOnly()
  }
  function drawState() {
    live.hidden = !connection
    if (connection) setBadge(badgeText || ('Live · ' + (macs.current?.name || 'the Mac')))
    drawRoutes()
  }
  /// Pairing: no addresses — the Mac shows a one-time code under its WebRTC front, and scanning it is the whole
  /// setup; on a desktop the pairing link can be pasted instead. (Connect a Mac, and the relay card's Pair this device.)
  function pairThisDevice() {
    if (pair) { pair(); return }
    // inside Cody's Android app: its own Add a Mac — Scan a code, or type it (a work-profile Cody pairs only there)
    if (phoneBridge()) { phoneAddMac().then((opened) => { if (!opened) liveStatus.textContent = 'Add a Mac could not be opened — long-press Cody’s icon › Add a Mac.' }); return }
    const link = prompt('On the Mac: Devices › Pair a device, then scan the code with your phone.\n\nOr paste the pairing link here:', '')
    if (!link) return
    try { const u = new URL(link.trim()); if (u.pathname.startsWith('/connect/')) { location.href = u.href; return } } catch {}
    alert('That is not a pairing link. It looks like https://…/connect/?room=…&code=….')
  }

  /// The sheet's controls, the Connect button (with no Mac paired) and the install hint.
  function installControls() {
    // ⋯ opens the sheet with everything that is not the arc: the state, connecting, installing, the build
    const sheet = document.getElementById('sheet')
    document.getElementById('more').addEventListener('click', () => { sheet.hidden = !sheet.hidden; if (!sheet.hidden) drawRoutes() })
    document.getElementById('relay-only')?.addEventListener('change', (e) => {
      setRelayOnly(e.target.checked)
      liveStatus.textContent = e.target.checked ? 'Relay only: reconnecting every Mac through a relay…' : 'Relay only is off: reconnecting…'
      if (connection?.rooms) macs.reconnectAll(); else if (connection) location.reload()
      drawRoutes()
    })
    const connect = document.getElementById('connect')
    if (!connection && connect && !servedByMac()) {
      // a real connection needs a Mac's pairing link (it carries the Mac's invite — no door page here since the
      // split, docs/features/site-split.md); the button says whether this browser is paired yet
      fetch('/api/pair/me', { cache: 'no-store' }).then((r) => r.status !== 401).catch(() => true).then((paired) => {
        connect.hidden = false
        if (!paired) connect.textContent = 'Pair this device with your Mac →'
      })
      connect.addEventListener('click', () => pairThisDevice())
    }
    registerOffline()   // the service worker, under the app's own root (core/offline.js)
    // installing it: the button Chrome and Edge offer, the hint Safari on iOS needs (core/offline.js)
    wireInstall(document.getElementById('install'), document.getElementById('install-hint'))
  }

  return { setBadge, drawRoutes, drawState, pairThisDevice, installControls }
}
